<script lang="ts">
  import { VList } from 'virtua/svelte'

  import BookshelfSlot from '$lib/components/nuread/BookshelfSlot.svelte'
  import { Skeleton } from '$lib/components/ui/skeleton/index.js'
  import BookOpen from '@lucide/svelte/icons/book-open'
  import BookMarked from '@lucide/svelte/icons/book-marked'
  import Sparkles from '@lucide/svelte/icons/sparkles'
  import ChevronRight from '@lucide/svelte/icons/chevron-right'
  import ChevronLeft from '@lucide/svelte/icons/chevron-left'
  import { servers } from '$lib/store'
  import { getLibraries } from '$lib/apis/library/api'
  import { serverHash } from '$lib/utils'


  let {
    serverid,
    series,
    header,
    loading = false,
    showLibraryName = false,
    showServerName = false,
    accent = 'default',
  }: {
    serverid?: string
    series: any[]
    header: string
    loading?: boolean
    showLibraryName?: boolean
    showServerName?: boolean
    accent?: 'default' | 'green' | 'sky'
  } = $props()

  let maxWidth = $state(0)
  let itemWidth = $state(279.5)
  let itemHeight = $state(416)
  let columns = $state(3)
  /** Keys are `libraryId` (single server) or `mediaId:libraryId` (global). */
  let libraryNames = $state<Record<string, string>>({})



  let list: VList<any>

  let thumbRatio = $state(1)
  let thumbOffset = $state(0)
  const showThumb = $derived(thumbRatio < 0.999)
  const placeholderCount = $derived(Math.max(columns, 3))
  const ghostCount = $derived(Math.min(Math.max(columns, 3), 5))
  const isEmpty = $derived(!loading && !(series?.length > 0))
  const coverStyle = $derived(
    `width:calc(${itemWidth}px - 1.875rem);height:calc(${itemHeight}px - 3.125rem)`
  )
  let hLim = $derived(maxWidth<370 ? 170 : 280)

  const headerClass = $derived(
    accent === 'green'
      ? 'text-latte-green dark:text-macchiato-green'
      : accent === 'sky'
        ? 'text-latte-sky dark:text-macchiato-sky'
        : 'text-latte-text dark:text-macchiato-text'
  )
  const chevronClass = $derived(
    accent === 'green'
      ? 'text-latte-surface1 transition-colors hover:text-latte-green dark:text-macchiato-surface1 dark:hover:text-macchiato-green'
      : accent === 'sky'
        ? 'text-latte-surface1 transition-colors hover:text-latte-sky dark:text-macchiato-surface1 dark:hover:text-macchiato-sky'
        : 'text-latte-surface1 transition-colors hover:text-latte-mauve dark:text-macchiato-surface1 dark:hover:text-macchiato-mauve'
  )
  const emptyCopy = $derived.by(() => {
    const h = header.toLowerCase()
    if (h.includes('continue')) {
      return {
        title: 'Nothing in progress',
        body: 'Open a series and your place will wait for you here.',
        Icon: BookMarked,
      }
    }
    if (h.includes('added') || h.includes('recent')) {
      return {
        title: 'Shelf is quiet',
        body: 'Fresh titles will land here once your libraries are scanned.',
        Icon: Sparkles,
      }
    }
    return {
      title: 'An empty stretch',
      body: 'Nothing to show on this shelf yet.',
      Icon: BookOpen,
    }
  })
  const EmptyIcon = $derived(emptyCopy.Icon)
  const emptyAccent = $derived(
    accent === 'green'
      ? {
          glow: 'bg-[radial-gradient(ellipse_at_center,color-mix(in_oklab,var(--color-latte-green)_28%,transparent),transparent_70%)] dark:bg-[radial-gradient(ellipse_at_center,color-mix(in_oklab,var(--color-macchiato-green)_22%,transparent),transparent_70%)]',
          icon: 'text-latte-green dark:text-macchiato-green',
          ring: 'ring-latte-green/25 dark:ring-macchiato-green/25',
          shelf: 'from-latte-green/40 via-latte-surface1/50 to-transparent dark:from-macchiato-green/35 dark:via-macchiato-surface1/40',
          ghost: 'border-latte-green/20 bg-latte-green/[0.04] dark:border-macchiato-green/20 dark:bg-macchiato-green/[0.06]',
        }
      : accent === 'sky'
        ? {
            glow: 'bg-[radial-gradient(ellipse_at_center,color-mix(in_oklab,var(--color-latte-sky)_28%,transparent),transparent_70%)] dark:bg-[radial-gradient(ellipse_at_center,color-mix(in_oklab,var(--color-macchiato-sky)_22%,transparent),transparent_70%)]',
            icon: 'text-latte-sky dark:text-macchiato-sky',
            ring: 'ring-latte-sky/25 dark:ring-macchiato-sky/25',
            shelf: 'from-latte-sky/40 via-latte-surface1/50 to-transparent dark:from-macchiato-sky/35 dark:via-macchiato-surface1/40',
            ghost: 'border-latte-sky/20 bg-latte-sky/[0.04] dark:border-macchiato-sky/20 dark:bg-macchiato-sky/[0.06]',
          }
        : {
            glow: 'bg-[radial-gradient(ellipse_at_center,color-mix(in_oklab,var(--color-latte-mauve)_26%,transparent),transparent_70%)] dark:bg-[radial-gradient(ellipse_at_center,color-mix(in_oklab,var(--color-macchiato-mauve)_20%,transparent),transparent_70%)]',
            icon: 'text-latte-mauve dark:text-macchiato-mauve',
            ring: 'ring-latte-mauve/25 dark:ring-macchiato-mauve/25',
            shelf: 'from-latte-mauve/40 via-latte-surface1/50 to-transparent dark:from-macchiato-mauve/35 dark:via-macchiato-surface1/40',
            ghost: 'border-latte-mauve/20 bg-latte-mauve/[0.04] dark:border-macchiato-mauve/20 dark:bg-macchiato-mauve/[0.06]',
          }
  )

  function locationFor(item: any):
    | { serverName?: string; serverTag?: string; libraryName?: string }
    | undefined {
    if (!showLibraryName && !showServerName) return undefined
    const mediaId = serverid ?? item.mediaId
    const libraryName = showLibraryName
      ? libraryNames[`${mediaId}:${item.libraryId}`] ??
        libraryNames[item.libraryId]
      : undefined
    const serverName = showServerName && mediaId
      ? $servers[mediaId]?.name
      : undefined
    const serverTag = showServerName && mediaId ? serverHash(mediaId) : undefined
    if (!serverName && !serverTag && !libraryName) return undefined
    return { serverName, serverTag, libraryName }
  }

  function syncThumb() {
    if (!list) return
    const scrollLeft = list.getScrollOffset()
    const scrollWidth = list.getScrollSize()
    const clientWidth = list.getViewportSize()
    if (scrollWidth <= 0 || clientWidth <= 0) return
    thumbRatio = Math.min(1, clientWidth / scrollWidth)
    const maxScroll = scrollWidth - clientWidth
    thumbOffset =
      maxScroll > 0 ? (scrollLeft / maxScroll) * (1 - thumbRatio) * 100 : 0
  }

  function scrollByItems(dir: -1 | 1) {
    if (!list || !series?.length) return
    const current = list.findItemIndex(list.getScrollOffset())
    const next = Math.max(0, Math.min(series.length - 1, current + dir * 2))
    list.scrollToIndex(next, { align: 'start', smooth: true })
  }

  $effect(() => {
    if (!showLibraryName) return
    let cancelled = false

    async function loadLibraries() {
      const map: Record<string, string> = {}
      const targets = serverid
        ? [$servers[serverid]].filter(Boolean)
        : Object.values($servers)

      await Promise.all(
        targets.map(async (server: any) => {
          const r = await getLibraries(server)
          for (const lib of r?.Libraries ?? []) {
            if (!lib?.id) continue
            map[lib.id] = lib.name
            map[`${server.uid}:${lib.id}`] = lib.name
          }
        })
      )
      if (!cancelled) libraryNames = map
    }

    loadLibraries()
    return () => {
      cancelled = true
    }
  })

  $effect(() => {
    if (!maxWidth) return

    for (let i = 9; i > 1; i--) {
      const h = ((maxWidth - 10) * 1.492) / i
      if (h > hLim) {
        itemHeight = h
        itemWidth = (maxWidth - 10) / i
        columns = i
        break
      }
    }
  })

  $effect(() => {
    void series
    void itemWidth
    void maxWidth
    requestAnimationFrame(syncThumb)
  })
</script>


<div class="w-full h-0" bind:clientWidth={null, v => (maxWidth = v!)}></div>

<div class="mt-10 flex items-stretch px-4 sm:px-10">
  <h1 class="text-xl font-bold {headerClass}">{header}</h1>
  <span class="grow"></span>
  <button
    type="button"
    class={chevronClass}
    disabled={loading || !series?.length}
    onclick={() => scrollByItems(-1)}
  >
    <ChevronLeft class="size-6 shrink-0" />
  </button>
  <span class="w-6"></span>
  <button
    type="button"
    class={chevronClass}
    disabled={loading || !series?.length}
    onclick={() => scrollByItems(1)}
  >
    <ChevronRight class="size-6 shrink-0" />
  </button>
</div>
{#if loading}
  <div
    class="flex overflow-hidden p-5"
    style={`height: ${itemHeight + 5}px;`}
    role="status"
    aria-label="Loading {header}"
  >
    {#each { length: placeholderCount } as _, i (i)}
      <div class="mx-2 shrink-0" style={`width: ${itemWidth}px;`}>
        <Skeleton class="rounded-t" style={coverStyle} />
        <Skeleton class="mt-2 h-4 w-3/4 rounded" />
        <Skeleton class="mt-1.5 h-3 w-1/3 rounded" />
      </div>
    {/each}
  </div>
{:else if isEmpty}
  <section
    class="shelf-empty relative isolate mx-4 overflow-hidden sm:mx-8"
    style={`height: ${itemHeight + 5}px;`}
    aria-label="{header}: empty"
  >
    <div
      class="shelf-empty__glow pointer-events-none absolute inset-0 {emptyAccent.glow}"
      aria-hidden="true"
    ></div>
    <div
      class="shelf-empty__haze pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-latte-base/40 to-transparent dark:from-macchiato-base/30"
      aria-hidden="true"
    ></div>

    <div
      class="relative z-[1] flex h-full items-end gap-3 overflow-hidden px-3 pb-8 pt-4 sm:gap-4 sm:px-6"
      aria-hidden="true"
    >
      {#each { length: ghostCount } as _, i (i)}
        {@const tilt = ((i % 3) - 1) * 2.2}
        {@const scale = 0.92 - (Math.abs(i - (ghostCount - 1) / 2) * 0.04)}
        <div
          class="shelf-empty__ghost mx-1 shrink-0"
          style={`width: ${itemWidth * 0.92}px; --ghost-delay: ${i * 0.12}s; --ghost-tilt: ${tilt}deg; --ghost-scale: ${scale};`}
        >
          <div
            class="shelf-empty__cover relative overflow-hidden rounded-t-md border border-dashed {emptyAccent.ghost}"
            style={`width: calc((${itemWidth}px - 1.875rem) * 0.92); height: calc((${itemHeight}px - 3.125rem) * 0.88);`}
          >
            <span
              class="shelf-empty__shimmer pointer-events-none absolute inset-0"
            ></span>
            <span
              class="absolute inset-x-[18%] top-[22%] h-px bg-latte-overlay0/40 dark:bg-macchiato-overlay0/35"
            ></span>
            <span
              class="absolute inset-x-[28%] top-[32%] h-px bg-latte-overlay0/30 dark:bg-macchiato-overlay0/25"
            ></span>
            <span
              class="absolute inset-x-[22%] top-[42%] h-px bg-latte-overlay0/20 dark:bg-macchiato-overlay0/18"
            ></span>
          </div>
          <div
            class="mt-2 h-2.5 w-2/3 rounded-full bg-latte-surface0/70 dark:bg-macchiato-surface0/60"
          ></div>
          <div
            class="mt-1.5 h-2 w-1/3 rounded-full bg-latte-surface0/45 dark:bg-macchiato-surface0/40"
          ></div>
        </div>
      {/each}
    </div>

    <div
      class="pointer-events-none absolute inset-x-0 bottom-[1.35rem] z-[1] h-px bg-gradient-to-r {emptyAccent.shelf}"
      aria-hidden="true"
    ></div>

    <div
      class="shelf-empty__copy absolute inset-0 z-[2] flex flex-col items-center justify-center px-6 text-center"
    >
      <div
        class="shelf-empty__badge mb-3 flex size-12 items-center justify-center rounded-2xl bg-latte-mantle/85 shadow-sm ring-1 backdrop-blur-sm {emptyAccent.ring} {emptyAccent.icon} dark:bg-macchiato-mantle/85"
      >
        <EmptyIcon class="size-5 opacity-90" strokeWidth={1.75} />
      </div>
      <p
        class="text-base font-semibold tracking-tight text-latte-text dark:text-macchiato-text"
      >
        {emptyCopy.title}
      </p>
      <p
        class="mt-1.5 max-w-xs text-sm leading-relaxed text-latte-text/65 dark:text-macchiato-text/65"
      >
        {emptyCopy.body}
      </p>
    </div>
  </section>
{:else}
  {#key series}
    <VList
      data={series}
      bind:this={list}
      style={`height: ${itemHeight + 5}px;`}
      class="p-5 scrollbar-none"
      horizontal
      getKey={(_, i) => i}
      onscroll={syncThumb}
    >
      {#snippet children(item, index)}
        <div class="mx-2 grid">
          <BookshelfSlot
            data={{
              series: item,
              dims: {
                height: itemHeight,
                width: itemWidth,
              },
              mediaId: serverid  ?? item.mediaId,
              libraryId: item.libraryId,
              location: locationFor(item),
              cdnUrl: $servers[serverid ?? item.mediaId].urls[0],
            }}
          />
        </div>
      {/snippet}
    </VList>
    {#if showThumb}
      <div class="relative mx-5 mb-1 h-1.5">
        <div
          class="absolute top-0 h-full rounded-full bg-gray-600/70"
          style="width: {thumbRatio * 100}%; left: {thumbOffset}%"
        ></div>
      </div>
    {/if}
  {/key}
{/if}

<style>
  .shelf-empty__glow {
    animation: shelf-empty-breathe 6.5s ease-in-out infinite;
  }

  .shelf-empty__ghost {
    transform: rotate(var(--ghost-tilt)) scale(var(--ghost-scale));
    transform-origin: 50% 100%;
    animation: shelf-empty-float 5.2s ease-in-out infinite;
    animation-delay: var(--ghost-delay);
    opacity: 0.55;
  }

  .shelf-empty__ghost:nth-child(even) {
    opacity: 0.38;
  }

  .shelf-empty__shimmer {
    background: linear-gradient(
      115deg,
      transparent 35%,
      color-mix(in oklab, var(--color-latte-overlay0) 18%, transparent) 50%,
      transparent 65%
    );
    background-size: 220% 100%;
    animation: shelf-empty-shimmer 4.8s ease-in-out infinite;
    animation-delay: var(--ghost-delay);
  }

  :global(.dark) .shelf-empty__shimmer {
    background: linear-gradient(
      115deg,
      transparent 35%,
      color-mix(in oklab, var(--color-macchiato-overlay0) 22%, transparent) 50%,
      transparent 65%
    );
    background-size: 220% 100%;
  }

  .shelf-empty__copy {
    animation: shelf-empty-rise 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .shelf-empty__badge {
    animation: shelf-empty-bob 5.5s ease-in-out infinite;
  }

  @keyframes shelf-empty-rise {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes shelf-empty-float {
    0%,
    100% {
      transform: rotate(var(--ghost-tilt, 0deg)) scale(var(--ghost-scale, 1))
        translateY(0);
    }
    50% {
      transform: rotate(var(--ghost-tilt, 0deg)) scale(var(--ghost-scale, 1))
        translateY(-7px);
    }
  }

  @keyframes shelf-empty-bob {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-6px);
    }
  }

  @keyframes shelf-empty-breathe {
    0%,
    100% {
      opacity: 0.75;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.04);
    }
  }

  @keyframes shelf-empty-shimmer {
    0%,
    100% {
      background-position: 120% 0;
    }
    50% {
      background-position: -20% 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .shelf-empty__glow,
    .shelf-empty__ghost,
    .shelf-empty__shimmer,
    .shelf-empty__copy,
    .shelf-empty__badge {
      animation: none;
    }
  }
</style>
